import * as React from 'react'
import {
  AffiliationRoleCondition,
  EngagementBandCondition,
} from '@/pages/SegmentAudienceConditionRows'
import { Alert } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AnyOfConditionRow,
  BooleanConditionRow,
  ChapterConditionRow,
  CompletedCourseConditionRow,
  MembershipStatusCondition,
} from '@/pages/SegmentBuiltInConditionRows'
import {
  CustomFieldConditionRow,
  customConditionField,
} from '@/pages/SegmentCustomFieldConditionRows'
import {
  DateRangeConditionRow,
  MoneyConditionRow,
  RenewalDueConditionRow,
  LapsedOnConditionRow,
} from '@/pages/SegmentRangeConditionRows'
import type { CustomField } from '@/lib/contacts/types'
import type { EventConditionField, SegmentEventField } from '@/pages/SegmentEventConditions'
import { SegmentAddConditionMenu } from '@/pages/SegmentAddConditionMenu'

type BuiltInConditionField =
  | 'membership_status'
  | 'membership_level'
  | 'autopay'
  | 'group_membership'
  | 'contact_type'
  | 'affiliation_role'
  | 'archived'
  | 'chapter_membership'
  | 'open_balance'
  | 'overdue_balance'
  | 'renewal_due'
  | 'member_since'
  | 'lapsed_on'
  | 'engagement_band'
  | 'completed_course'
export type ConditionField = BuiltInConditionField | EventConditionField | `custom_field:${number}`

export interface SegmentCondition {
  field: ConditionField
  operator: string
  values: Array<string | number>
}
interface ConditionCollections {
  conditions: SegmentCondition[]
  membershipLevels: Array<{ id: number; name: string }>
  groups: Array<{ id: number; name: string }>
  customFields: CustomField[]
  chapters: Array<{ id: number; name: string; archived?: boolean }>
  courses: Array<{ id: number; title: string; status?: string }>
}
interface Props extends ConditionCollections {
  eventConditions?: React.ReactNode
  eventFields?: SegmentEventField[]
  name: string
  nameError?: string
  conditionError?: string
  saveError: boolean
  onNameChange: (name: string) => void
  onConditionsChange: React.Dispatch<React.SetStateAction<SegmentCondition[]>>
}
export function SegmentRuleFields({
  eventConditions,
  eventFields = [],
  name,
  conditions,
  nameError,
  conditionError,
  saveError,
  membershipLevels,
  groups,
  customFields,
  chapters,
  courses,
  onNameChange,
  onConditionsChange,
}: Props) {
  return (
    <div className="flex max-w-2xl flex-col gap-5">
      {saveError && !nameError && !conditionError && <Alert>Segment could not be saved.</Alert>}
      <div className="flex items-center gap-3">
        <Label htmlFor="segment-name" className="w-16 shrink-0 text-muted-foreground">
          Name
        </Label>
        <div className="w-full max-w-xs">
          <Input
            id="segment-name"
            className="h-8"
            value={name}
            aria-invalid={Boolean(nameError)}
            onChange={(event) => onNameChange(event.target.value)}
          />
          {nameError && <p className="pt-1 text-caption text-destructive">{nameError}</p>}
        </div>
      </div>
      <SegmentConditionsEditor
        eventConditions={eventConditions}
        eventFields={eventFields}
        conditions={conditions}
        membershipLevels={membershipLevels}
        groups={groups}
        customFields={customFields}
        chapters={chapters}
        courses={courses}
        onChange={onConditionsChange}
      />
      {conditionError && <p className="pl-11 text-caption text-destructive">{conditionError}</p>}
    </div>
  )
}

function SegmentConditionsEditor({
  eventConditions,
  eventFields,
  conditions,
  membershipLevels,
  groups,
  customFields,
  chapters,
  courses,
  onChange,
}: ConditionCollections & {
  eventConditions?: React.ReactNode
  eventFields: SegmentEventField[]
  onChange: React.Dispatch<React.SetStateAction<SegmentCondition[]>>
}) {
  const update = (field: ConditionField, next?: SegmentCondition) =>
    onChange((current) => {
      const withoutField = current.filter((condition) => condition.field !== field)
      return next ? [...withoutField, next] : withoutField
    })

  return (
    <div>
      <div className="mb-2">
        <Label className="text-muted-foreground">Conditions</Label>
      </div>
      <MembershipStatusCondition conditions={conditions} update={update} />
      <div className="space-y-2 pl-11">
        <EngagementBandCondition conditions={conditions} update={update} />
        {(['open_balance', 'overdue_balance'] as const).map((field) => {
          const condition = findCondition(conditions, field)
          return condition ? (
            <MoneyConditionRow
              key={field}
              condition={condition}
              onChange={(next) => update(field, next)}
              onRemove={() => update(field)}
            />
          ) : null
        })}
        {findCondition(conditions, 'renewal_due') && (
          <RenewalDueConditionRow
            condition={findCondition(conditions, 'renewal_due')!}
            onChange={(next) => update('renewal_due', next)}
            onRemove={() => update('renewal_due')}
          />
        )}
        {findCondition(conditions, 'member_since') && (
          <DateRangeConditionRow
            condition={findCondition(conditions, 'member_since')!}
            onChange={(next) => update('member_since', next)}
            onRemove={() => update('member_since')}
          />
        )}
        <LapsedOnCondition conditions={conditions} update={update} />
        <ReferenceConditionRows
          conditions={conditions}
          membershipLevels={membershipLevels}
          groups={groups}
          courses={courses}
          chapters={chapters}
          update={update}
        />
        <AffiliationRoleCondition conditions={conditions} update={update} />
        {findCondition(conditions, 'contact_type') && (
          <AnyOfConditionRow
            label="Contact type"
            condition={findCondition(conditions, 'contact_type')!}
            options={[
              { value: 'person', label: 'Person' },
              { value: 'organization', label: 'Organization' },
            ]}
            onChange={(next) => update('contact_type', next)}
            onRemove={() => update('contact_type')}
          />
        )}
        {(['autopay', 'archived'] as const).map((field) => {
          const condition = findCondition(conditions, field)
          return condition ? (
            <BooleanConditionRow
              key={field}
              field={field}
              condition={condition}
              onChange={(next) => update(field, next)}
              onRemove={() => update(field)}
            />
          ) : null
        })}
        <CustomFieldConditionRows
          conditions={conditions}
          customFields={customFields}
          update={update}
        />
        {eventConditions}
        <SegmentAddConditionMenu
          conditions={conditions}
          membershipLevels={membershipLevels}
          groups={groups}
          customFields={customFields}
          courses={courses}
          eventFields={eventFields}
          update={update}
          append={(next) => onChange((current) => [...current, next])}
        />
      </div>
    </div>
  )
}
function ReferenceConditionRows({
  conditions,
  membershipLevels,
  groups,
  courses,
  chapters,
  update,
}: {
  conditions: SegmentCondition[]
  membershipLevels: Array<{ id: number; name: string }>
  groups: Array<{ id: number; name: string }>
  courses: Array<{ id: number; title: string; status?: string }>
  chapters: Array<{ id: number; name: string; archived?: boolean }>
  update: (field: ConditionField, next?: SegmentCondition) => void
}) {
  return (
    <>
      {findCondition(conditions, 'membership_level') && (
        <AnyOfConditionRow
          label="Membership Level"
          allowInverse
          condition={findCondition(conditions, 'membership_level')!}
          options={membershipLevels.map((level) => ({ value: level.id, label: level.name }))}
          onChange={(next) => update('membership_level', next)}
          onRemove={() => update('membership_level')}
        />
      )}
      {findCondition(conditions, 'group_membership') && (
        <AnyOfConditionRow
          label="Group membership"
          condition={findCondition(conditions, 'group_membership')!}
          options={groups.map((group) => ({ value: group.id, label: group.name }))}
          onChange={(next) => update('group_membership', next)}
          onRemove={() => update('group_membership')}
        />
      )}
      {findCondition(conditions, 'completed_course') && (
        <CompletedCourseConditionRow
          condition={findCondition(conditions, 'completed_course')!}
          courses={courses}
          onChange={(next) => update('completed_course', next)}
          onRemove={() => update('completed_course')}
        />
      )}
      {findCondition(conditions, 'chapter_membership') && (
        <ChapterConditionRow
          condition={findCondition(conditions, 'chapter_membership')!}
          chapters={chapters}
          onChange={(next) => update('chapter_membership', next)}
          onRemove={() => update('chapter_membership')}
        />
      )}
    </>
  )
}
function CustomFieldConditionRows({
  conditions,
  customFields,
  update,
}: {
  conditions: SegmentCondition[]
  customFields: CustomField[]
  update: (field: ConditionField, next?: SegmentCondition) => void
}) {
  return customFields.map((field) => {
    const conditionField = customConditionField(field)
    const condition = findCondition(conditions, conditionField)
    return condition ? (
      <CustomFieldConditionRow
        key={field.id}
        field={field}
        condition={condition}
        onChange={(next) => update(conditionField, next)}
        onRemove={() => update(conditionField)}
      />
    ) : null
  })
}

function LapsedOnCondition({
  conditions,
  update,
}: {
  conditions: SegmentCondition[]
  update: (field: ConditionField, next?: SegmentCondition) => void
}) {
  const condition = findCondition(conditions, 'lapsed_on')
  return condition ? (
    <LapsedOnConditionRow
      condition={condition}
      onChange={(next) => update('lapsed_on', next)}
      onRemove={() => update('lapsed_on')}
    />
  ) : null
}
function findCondition(conditions: SegmentCondition[], field: ConditionField) {
  return conditions.find((condition) => condition.field === field)
}
